// Maison Dodji — i18n
// French is the master reference. English is an editorial adaptation,
// not a literal translation. All copy below is approved.

export const translations = {
  fr: {
    locale: "fr-FR",

    nav: {
      maison: "Maison",
      collection: "Collection",
      univers: "Études Privées",
      contact: "Contact",
    },

    cta: {
      eyebrow: "Présentation privée",
      titleDefault: "Reconnaître la pièce qui vous ressemble.",
      titleContact: "Une rencontre, un lieu, une pièce.",
      body:
        "Maison Dodji privilégie une relation directe, fondée sur la présentation des pièces, la compréhension des lieux et la qualité des échanges. La maison reçoit sur rendez-vous.",
      button: "Demander une présentation privée",
    },

    footer: {
      brand: "Maison Dodji",
      tagline:
        "Mobilier singulier conçu pour l'intérieur privé. Une maison parisienne où chaque pièce installe une présence — et accueille le mouvement horloger en son cœur.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      copyright: "© {year} Maison Dodji — Tous droits réservés",
      baseline: "Mobilier singulier. Présence horlogère.",
    },

    home: {
      eyebrowYear: "2026",
      eyebrowEdition: "Édition régulière · Paris",
      title: "Maison\u00A0Dodji",
      heroBrand: "Maison Dodji Paris",
      tagline: "Mobilier singulier. Présence horlogère.",
      intro:
        "Maison Dodji compose des objets de présence à la frontière du design, du mobilier et de l'horlogerie. Pensée comme une collection de formes à habiter, la maison invite chacun à reconnaître la pièce qui lui ressemble.",
      heroDescription:
        "Des pièces de mobilier conçues pour intégrer, protéger et mettre en présence les objets horlogers.",
      ctaCollection: "Découvrir la collection",
      ctaContact: "Présentation privée",
      heroCtaPieces: "Découvrir la collection",
      heroCtaEtudes: "Écrire à la Maison",
      featureEyebrow: "Pièce signature",
      featureCta: "Découvrir",

      matterLine1: "Pièces uniques et séries très limitées.",
      matterLine2:
        "Atelier français — bois laqué, métal gravé, modules mécaniques suisses.",

      statementEyebrow: "La maison",
      statementTitle:
        "Donner au mobilier singulier sa juste place dans l'intérieur privé.",
      statementBody:
        "Maison Dodji conçoit des pièces de mobilier singulier pensées pour l'intérieur privé. Chaque meuble installe une présence — une silhouette, une assise, un rythme — où le mouvement horloger trouve sa place et son rituel.",
      statementCta: "Lire la maison",

      collectionEyebrow: "La collection",
      collectionTitleLine1: "Cinq modèles,",
      collectionTitleLine2: "cinq présences,",
      collectionTitleLine3:
        "cinq manières d'inscrire le mouvement dans l'espace.",
      collectionAside:
        "Maison Dodji ne juxtapose pas des objets\u00A0; la maison construit un langage. Chaque pièce possède son rythme, sa silhouette et son intensité.",

      featuredEyebrow: "Pièces choisies",
      featuredAll: "Voir toutes les pièces",
      featuredCta: "Découvrir",

      pathsEyebrow: "Deux voies",
      pathPiecesTitle: "Les Pièces",
      pathPiecesBody:
        "Des créations définies, disponibles en pièces uniques ou en séries très limitées.",
      pathPiecesCta: "Découvrir les pièces",
      pathEtudesTitle: "Études Privées",
      pathEtudesBody:
        "Pour les demandes qui ne relèvent d'aucun modèle existant et nécessitent une instruction particulière.",
      pathEtudesCta: "Présenter une étude",

      universeEyebrow: "L'univers",
      universeTitle:
        "Une grammaire d'objets pensés pour durer, pour habiter, pour transmettre.",
      universePillars: [
        ["Présence", "ce qui occupe le lieu sans le saturer"],
        ["Forme", "la silhouette comme premier langage"],
        ["Silhouette", "une lecture immédiate, calme"],
        ["Rythme", "la cadence qui structure l'espace"],
        ["Résonance", "l'objet qui répond à l'intérieur"],
        ["Transmission", "l'objet que l'on lègue"],
      ],
      universeCta: "Explorer l'univers",

      placesEyebrow: "Lieux",
      placesTitle:
        "Pensés pour les intérieurs assumés, les bureaux silencieux, les lieux où l'objet se choisit.",
      placesBlocks: [
        {
          eyebrow: "Fabrication",
          body:
            "Atelier français — bois laqué, métal gravé, modules mécaniques suisses.",
        },
        {
          eyebrow: "Distribution",
          body:
            "Sur rendez-vous, sur invitation. Présentation privée uniquement.",
        },
        {
          eyebrow: "Engagement",
          body:
            "Édition régulière, série limitée à la demande, suivi par la maison.",
        },
      ],
    },

    collection: {
      eyebrow: "La collection",
      title: "Cinq pièces, un langage.",
      intro:
        "Cinq modèles, cinq présences, cinq manières d'inscrire le mouvement horloger dans l'espace. Maison Dodji ne juxtapose pas des objets\u00A0; la maison construit un langage. Chaque pièce possède son rythme, sa silhouette et son intensité.",
      asideLine1: "Prix de collection.",
      asideLine2: "Présentation privée sur rendez-vous.",
      renderingNote:
        "Les visuels présentés ont été volontairement réalisés comme des rendus de projection, afin d'exprimer la dimension personnalisable de chaque pièce.",
      pieceLabel: "Pièce N°",
      pieceCount: (n) => `${n} montre${n > 1 ? "s" : ""}`,
      cta: "Découvrir",
    },

    product: {
      back: "← Collection",
      pieceLabel: "Pièce N°",
      specsTitle: "Caractéristiques",
      specOrdinal: (i) => `0${i + 1}`,
      editionLabel: "Disponibilité sur rendez-vous",
      madeToOrder: "Chaque pièce est réalisée à la commande.",
      pricingFootnote:
        "*Le prix indiqué correspond au point d'entrée du modèle. Les finitions, associations de couleurs et personnalisations peuvent faire évoluer le prix.",
      personalizationEyebrow: "Personnalisation",
      personalizationTitle: "Personnalisation",
      personalizationBody:
        "Les couleurs, finitions et certains détails peuvent être personnalisés dans le respect de l'architecture du modèle et des contraintes techniques Maison Dodji. Les demandes particulières sont étudiées au cas par cas.",
      personalizationCanChooseTitle: "Vous pouvez choisir",
      personalizationCanChoose: [
        "Couleurs",
        "Associations de teintes",
        "Finitions",
      ],
      personalizationValidationTitle: "Sous validation Maison Dodji",
      personalizationValidation: [
        "Matières spéciales",
        "Graphismes & inscriptions",
        "Contraintes particulières",
      ],
      personalizationArchitectureTitle:
        "L'architecture reste inchangée",
      personalizationArchitecture: [
        "Proportions",
        "Structure",
        "Configuration horlogère",
      ],
      personalizationCta: "Envisager une personnalisation → Études Privées",
      ctaContact: "Demander cette pièce",
      ctaCollection: "Voir la collection",
      otherEyebrow: "Autres pièces",
      otherTitle: "Reconnaître la pièce qui vous ressemble.",
      linkAll: "Toute la collection",
    },

    maison: {
      eyebrow: "La maison",
      title: "Une maison parisienne dédiée au mobilier singulier.",
      intro1:
        "Maison Dodji est née à Paris d'une intuition simple\u00A0: un intérieur privé mérite des pièces qui installent une présence. Des meubles singuliers, dessinés comme des formes — capables d'accueillir un rituel, une matière, un mouvement horloger.",
      intro2:
        "À la frontière du design, du mobilier et de l'horlogerie, la maison conçoit des pièces singulières — pensées pour habiter l'espace plutôt que pour s'y dissimuler. Chaque modèle est une forme à reconnaître, une présence à accueillir, un rythme à installer.",
      introAside:
        "Choisir une pièce Maison Dodji, ce n'est pas posséder un objet de plus. C'est reconnaître une forme, accepter un rythme, faire place à une présence.",
      gestureEyebrow: "Le geste",
      gestureTitle: "Habiter l'objet, plutôt que le posséder.",
      gestureBody:
        "Chaque pièce est conçue comme une forme à habiter\u00A0: un volume qui structure le lieu, une matière qui dialogue avec la lumière, un mouvement intérieur qui devient présence.",
      pillarsEyebrow: "Trois axes",
      pillarsTitle: "Trois axes structurent la maison.",
      pillars: [
        {
          eyebrow: "Design",
          title: "Forme et silhouette",
          body:
            "Des pièces dessinées pour la lecture immédiate, pour la justesse du volume, pour la précision du détail.",
        },
        {
          eyebrow: "Mobilier",
          title: "Présence et lieu",
          body:
            "Un meuble singulier qui ne se contente pas d'accompagner&nbsp;: il structure, il habite, il s'installe.",
        },
        {
          eyebrow: "Horlogerie",
          title: "Mouvement et durée",
          body:
            "Des modules mécaniques suisses au service de la montre mécanique, dans un écrin pensé pour la durée.",
        },
      ],
      transmissionEyebrow: "Transmission",
      transmissionTitle:
        "Un objet pensé pour vivre, pour durer, pour se transmettre.",
      transmissionBody:
        "Chaque pièce Maison Dodji est conçue pour traverser le temps — non comme un produit qui s'use, mais comme un meuble qui s'installe, qui accompagne, qui se transmet. Le mouvement automatique trouve sa place\u00A0; la maison trouve la sienne.",
      transmissionCta: "Voir la collection",
      signatureAlt: "Lumumba — pièce Maison Dodji",
    },

    univers: {
      eyebrow: "L'univers",
      title: "Une maison, un langage, un monde.",
      doctrine:
        "Maison Dodji conçoit des pièces de mobilier d'usage où la montre mécanique devient présence, rituel et objet d'intérieur.",
      intro:
        "L'univers Maison Dodji se lit dans la matière, dans la silhouette, dans la manière dont la pièce s'inscrit dans le lieu. La maison s'adresse à ceux qui composent leur intérieur avec exigence et qui souhaitent y accueillir le mouvement horloger dans une pièce de mobilier pensée comme une présence.",
      introAsideL1: "Le mobilier comme cadre.",
      introAsideL2: "Le mouvement comme rythme.",
      introAsideL3: "Le lieu comme résonance.",
      markersEyebrow: "Repères",
      markersTitle: "Là où la maison s'inscrit.",
      markers: [
        {
          num: "01",
          title: "Design",
          body:
            "Une recherche de la silhouette juste — sculpturale sans démonstration, présente sans saturation.",
        },
        {
          num: "02",
          title: "Mobilier",
          body:
            "Un objet pensé comme un meuble&nbsp;: assise, présence, durée. Une pièce du lieu, choisie pour ce qu'elle installe.",
        },
        {
          num: "03",
          title: "Horlogerie",
          body:
            "Le mouvement mécanique au cœur de l'objet&nbsp;: modules mécaniques suisses, montres automatiques, mouvement continu.",
        },
        {
          num: "04",
          title: "Intérieurs",
          body:
            "Bureaux silencieux, salons composés, intérieurs privés, lieux où chaque objet est choisi avec l'exigence d'une œuvre.",
        },
      ],
      collabEyebrow: "Collaborations",
      collabTitle: "Présentations, lieux, dialogues.",
      collabBody1:
        "Maison Dodji accueille un nombre restreint de collaborations avec des architectes d'intérieur, des décorateurs, des lieux privés, des projets résidentiels d'exception, des artistes et des galeries choisies comme espaces de mise en scène. Certains acteurs horlogers peuvent également être associés lorsque le dialogue respecte l'esprit de la maison. Chaque dialogue se construit dans la durée, autour de la pièce — non autour de la transaction.",
      collabBody2:
        "Les présentations privées se déroulent dans des lieux dédiés ou directement au sein des intérieurs des clients. La maison se déplace lorsque la pièce le justifie.",
      collabSpacesEyebrow: "Espaces de collaboration",
      collabSpacesTitle: "Mises en scène privées.",
      collabSpacesBody:
        "Maison Dodji ouvre un espace de collaboration avec des architectes d'intérieur, décorateurs, galeries et lieux privés afin de mettre en scène ses pièces dans des environnements plus denses, plus habités, où le mobilier dialogue avec les matières, les œuvres, la lumière et l'usage.",
      stagingNoteEyebrow: "Lumière à venir",
      stagingNote:
        "Les visuels actuels présentent les pièces dans une lumière volontairement neutre. Les collaborations à venir permettront de les inscrire dans des intérieurs plus denses, afin d'exprimer leur rapport aux matières, aux œuvres, aux meubles et aux lieux.",
      privateStudyEyebrow: "Études privées",
      privateStudyBody:
        "Certaines études s'ouvrent en privé, à la suite d'une conversation avec la Maison. Elles ne sont pas visibles sur le site.",
      commissionsSignal:
        "Certaines Études privées donnent naissance à des commandes singulières, conçues pour une personne, un usage et un lieu précis. Lorsqu'une réalisation peut être rendue publique, Maison Dodji Paris en conserve progressivement la trace sous la forme d'une Private Commission.",

      // ── Architecture de Mutation ─────────────────────────────────────
      mutationEyebrow: "Architecture de Mutation",
      mutationTitle:
        "Au-delà de l'Étude Privée, certaines visions exigent une Architecture de Mutation.",
      mutationBody:
        "Certaines intentions ne peuvent être obtenues par la couleur, la matière ou la finition seules. Maison Dodji intervient alors sur la forme elle-même. À partir du noyau fonctionnel d'une pièce existante, une Architecture de Mutation est développée : nouvelles proportions, volumes, reliefs, raccords et enveloppe sont conçus pour faire émerger une présence qui n'existait pas auparavant.",
      mutationCredo: [
        "Le noyau demeure.",
        "L'enveloppe évolue.",
        "La présence mute.",
      ],
      mutationPassageEyebrow: "Le passage",
      mutationPassageTitle: "De l'interprétation à la transformation.",
      mutationPassageBody:
        "L'Architecture de Mutation n'est ni une option de personnalisation ni une nouvelle collection. Elle intervient lorsqu'une Étude Privée atteint un niveau d'intention qui nécessite de dépasser l'architecture originelle de la pièce. La forme Maison Dodji devient alors le point de départ d'une nouvelle construction.",
      mutationSequence: [
        {
          num: "01",
          title: "La vision",
          body: "Définir ce que la pièce doit devenir.",
        },
        {
          num: "02",
          title: "Le noyau",
          body: "Préserver l'architecture fonctionnelle Maison Dodji.",
        },
        {
          num: "03",
          title: "L'Architecture de Mutation",
          body: "Concevoir les nouveaux volumes, raccords et proportions.",
        },
        {
          num: "04",
          title: "L'enveloppe",
          body: "Matérialiser cette nouvelle architecture.",
        },
        {
          num: "05",
          title: "La mutation",
          body: "Faire apparaître la pièce définitive.",
        },
      ],
      mutationClosing:
        "Une Étude Privée interprète une pièce. Une Architecture de Mutation peut en faire apparaître une nouvelle expression.",
      locations: [
        ["Sur rendez-vous", "Présentations privées"],
        ["Sur déplacement", "Selon le projet"],
        ["Sur invitation", "Édition régulière"],
      ],
      imageAlts: {
        sankara: "Sankara — présence horizontale",
        rosa: "Rosa — pièce circulaire",
        quincy: "Quincy — densité du quatuor",
      },
    },

    contact: {
      eyebrow: "Présentation privée",
      byAppointment: "Sur rendez-vous",
      title: "Écrire à la Maison",
      intro:
        "Pour toute demande de présentation privée, d'acquisition ou de collaboration, merci d'écrire à la Maison via le formulaire ci-dessous ou à l'adresse suivante\u00A0:",
      asideL1: "Une relation,",
      asideL2: "non une transaction.",

      processEyebrow: "Le déroulé",
      steps: [
        {
          num: "01",
          title: "Échange initial",
          body:
            "Nous vous répondons sous 48 heures. Un premier échange permet de comprendre votre lieu, votre intention et la pièce qui vous intéresse.",
        },
        {
          num: "02",
          title: "Présentation privée",
          body:
            "Sur rendez-vous, dans des lieux dédiés ou directement chez vous lorsque le projet le justifie. La pièce est présentée dans son contexte d'accueil.",
        },
        {
          num: "03",
          title: "Suivi de la maison",
          body:
            "Chaque pièce est conçue, livrée et accompagnée dans la durée. La maison reste votre interlocuteur unique.",
        },
      ],

      asideEyebrow: "Contact",
      writeEyebrow: "Écrire à la maison",
      privateOrderEyebrow: "Commande privée",
      privateOrderPdfLabel: "Télécharger la fiche commande privée",
      privateOrderPdfHint: "PDF · FR / EN · Cycle 2026",

      form: {
        nom: "Nom",
        nomPlaceholder: "Votre nom",
        email: "Email",
        emailPlaceholder: "vous@adresse.com",
        ville: "Ville / Pays",
        villePlaceholder: "Paris, France",
        objet: "Objet de la demande",
        piece: "Pièce d'intérêt",
        pieceNoPreference: "— Aucune préférence",
        message: "Message",
        messagePlaceholder:
          "Quelques mots sur votre projet, votre lieu, votre intention…",
        note: "Nous reviendrons vers vous dans les meilleurs délais.",
        submit: "Envoyer la demande",
        submitting: "Envoi…",
      },
      objects: [
        "Acquisition",
        "Pièce personnalisée",
        "Étude Privée",
        "Collaboration",
        "Autre",
      ],

      successEyebrow: "Message reçu",
      successTitle: "Merci. La Maison vous répondra prochainement.",
      successBody:
        "Votre demande a bien été reçue. Maison Dodji reviendra vers vous dans les meilleurs délais afin de poursuivre cet échange.",

      toastSuccess: "Votre demande a bien été reçue par la maison.",
      toastErrorRequired: "Merci de renseigner les informations essentielles.",
      toastErrorGeneric: "Une erreur est survenue. Merci de réessayer.",

      closingEyebrow: "Maison Dodji",
      closingTitle: "Mobilier singulier. Présence horlogère.",
      closingBody:
        "Sur rendez-vous. La maison se déplace lorsque la pièce le justifie.",
    },
    space: {
      metaTitle: "Chapitre — Maison Dodji",
      metaDescription: "Chapitre d'étude. Espace privé de la Maison Dodji.",
      returnMaison: "Retour à la Maison",
      sealedBanner: "Chapitre clos",
      sealedOn: "Scellé le",
      studyOpened: "Étude ouverte le",
      chapters: {
        salutation: "Salutation",
        compris: "Ce que la Maison a compris",
        references: "Références et intentions",
        questions: "Questions à la Maison",
        rythme: "Rythme",
        correspondance: "Correspondance",
        proposition: "Proposition",
        closing: "Le chapitre est clos.",
        welcome: "Bienvenue dans votre Project Space Maison Dodji",
        projectOwner: "Le commanditaire",
        residence: "La résidence",
        architecture: "L'architecture",
        watchCollection: "La collection horlogère",
        existingSafe: "Le coffre existant",
        futureCollection: "La collection à venir",
        lifestyle: "Le mode de vie",
        designReferences: "Références de design",
        architect: "L'architecte",
        interiorDesigner: "L'architecte d'intérieur",
        photos: "Photographies des lieux",
        measurements: "Mesures et dimensions",
        technicalConstraints: "Contraintes techniques",
        maisonObservations: "Observations de la Maison Dodji",
        maisonQuestions: "Questions de la Maison",
        projectConclusion: "Conclusion du projet",
      },
      fields: {
        salutation: "Salutation", body: "Introduction",
        fullName: "Nom", city: "Ville", contact: "Contact",
        background: "Parcours",
        address: "Adresse", style: "Style", era: "Époque",
        rooms: "Pièces principales", atmosphere: "Atmosphère",
        volumes: "Volumes", materials: "Matériaux", light: "Lumière",
        ambiance: "Ambiance",
        summary: "Résumé", brand: "Marque", reference: "Référence",
        notes: "Notes", intent: "Intention", horizon: "Horizon",
        hasSafe: "Coffre existant", model: "Modèle",
        capacity: "Capacité", location: "Emplacement",
        limitations: "Limitations",
        rhythm: "Rythme de vie", travel: "Voyages", interests: "Centres d'intérêt",
        imageUrl: "Image (URL)", caption: "Légende",
        name: "Nom", agency: "Agence",
        label: "Zone", dimensions: "Dimensions",
        access: "Accès", electrical: "Électricité",
        humidity: "Hygrométrie", security: "Sécurité", other: "Autres",
        prompt: "Question", nextSteps: "Prochaines étapes",
        observations: "Observations de la Maison",
        yes: "Oui", no: "Non", na: "Sans objet",
        addItem: "Ajouter",
      },
      brief: {
        docTitle: "Brief architectural — Maison Dodji",
        eyebrow: "Maison Dodji — Brief architectural",
        confidential: "Document confidentiel — Établi par la Maison Dodji",
        projectNumber: "Numéro de projet",
        client: "Commanditaire",
        title: "Titre du projet",
        date: "Date d'ouverture",
        piece: "Pièce",
        pieceNumber: "Numéro de pièce",
        summary: "Synthèse",
        chapters: "Chapitres complétés",
        openBrief: "Consulter le Brief architectural",
        print: "Imprimer",
      },
      colophon: {
        title: "Colophon",
        project: "Projet",
        piece: "Pièce",
        opened: "Étude ouverte",
        sealed: "Chapitre clos",
        pieceNumber: "Numéro de pièce",
        signature: "Signature",
      },
      fixtureNotice:
        "Fixture technique privée — n'est pas un projet authentique de la Maison Dodji.",
      notFoundTitle: "Ce chapitre n'existe pas.",
      notFoundBody:
        "Les chapitres de la Maison Dodji ne sont pas publics. Ils sont ouverts à une personne, par la Maison, après conversation.",
    },
    commission: {
      metaTitle: "Maison Dodji Paris — Private Commission",
      openingHouse: "Maison Dodji Paris",
      openingSeries: "Private Commission 01",
      returnMaison: "Retour à la Maison",
      oneOfOne: "One of one",
      statusEyebrow: "État du projet",
      chapterEyebrow: "Chapitre",
      chaptersHeading: "Chapitres du projet",
      finalHeading: "Présentation finale",
      pieceLabel: "Pièce",
      pieceNumberLabel: "Référence",
      closingSignatureLine1: "Maison Dodji Paris",
      closingSignatureLine2: "Mobilier singulier. Autorité discrète.",
      closingSignatureLine3: "Le temps, à sa place.",
      awaitingTitle: "Cette page n'est pas encore ouverte au public.",
      awaitingBody:
        "Maison Dodji conserve la trace de cette création. Sa publication n'a pas encore été autorisée.",
    },
  },

  en: {
    locale: "en-GB",

    nav: {
      maison: "Maison",
      collection: "Collection",
      univers: "Private Studies",
      contact: "Contact",
    },

    cta: {
      eyebrow: "Private Presentation",
      titleDefault: "Recognize the piece that feels like you.",
      titleContact: "A meeting, a place, a piece.",
      body:
        "Maison Dodji favors a direct relationship, grounded in the presentation of the pieces, the understanding of places, and the quality of exchange. The maison welcomes inquiries by appointment.",
      button: "Request a private presentation",
    },

    footer: {
      brand: "Maison Dodji",
      tagline:
        "Singular furniture conceived for the private interior. A Parisian maison where each piece installs a presence — and welcomes horological movement at its heart.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      copyright: "© {year} Maison Dodji — All rights reserved",
      baseline: "Singular furniture. Horological presence.",
    },

    home: {
      eyebrowYear: "2026",
      eyebrowEdition: "Regular edition · Paris",
      title: "Maison\u00A0Dodji",
      heroBrand: "Maison Dodji Paris",
      tagline: "Singular furniture. Horological presence.",
      intro:
        "Maison Dodji creates objects of presence at the intersection of design, furniture and horology. Conceived as a collection of forms to inhabit, the maison invites each person to recognize the piece that feels like their own.",
      heroDescription:
        "Furniture pieces conceived to integrate, protect and give presence to horological objects.",
      ctaCollection: "Discover the collection",
      ctaContact: "Request a private presentation",
      heroCtaPieces: "Discover the collection",
      heroCtaEtudes: "Write to the Maison",
      featureEyebrow: "Signature piece",
      featureCta: "Discover",

      matterLine1: "One-of-a-kind pieces and very limited editions.",
      matterLine2:
        "French workshop — lacquered wood, engraved metal, Swiss mechanical modules.",

      statementEyebrow: "The maison",
      statementTitle:
        "Giving singular furniture its rightful place in the private interior.",
      statementBody:
        "Maison Dodji conceives singular furniture for the private interior. Each piece installs a presence — a silhouette, a grounding, a rhythm — within which horological movement finds its place and its ritual.",
      statementCta: "Read the maison",

      collectionEyebrow: "The collection",
      collectionTitleLine1: "Five models,",
      collectionTitleLine2: "five presences,",
      collectionTitleLine3:
        "five ways of inscribing movement into space.",
      collectionAside:
        "Maison Dodji does not juxtapose objects; the maison constructs a language. Each piece carries its own rhythm, silhouette and intensity.",

      featuredEyebrow: "Selected pieces",
      featuredAll: "See all pieces",
      featuredCta: "Discover",

      pathsEyebrow: "Two paths",
      pathPiecesTitle: "The Pieces",
      pathPiecesBody:
        "Defined creations, available as one-of-a-kind pieces or in very limited editions.",
      pathPiecesCta: "Discover the pieces",
      pathEtudesTitle: "Private Studies",
      pathEtudesBody:
        "For requests that fall outside any existing model and require a particular consideration.",
      pathEtudesCta: "Open a study",

      universeEyebrow: "Universe",
      universeTitle:
        "A grammar of objects conceived to endure, to inhabit, to transmit.",
      universePillars: [
        ["Presence", "what occupies a place without saturating it"],
        ["Form", "silhouette as a first language"],
        ["Silhouette", "an immediate, quiet reading"],
        ["Rhythm", "the cadence that structures space"],
        ["Resonance", "the object that responds to the interior"],
        ["Transmission", "the object one passes on"],
      ],
      universeCta: "Explore the universe",

      placesEyebrow: "Places",
      placesTitle:
        "Conceived for assured interiors, quiet offices, and the places where an object is chosen.",
      placesBlocks: [
        {
          eyebrow: "Craftsmanship",
          body:
            "French workshop — lacquered wood, engraved metal, Swiss mechanical modules.",
        },
        {
          eyebrow: "Presentation",
          body:
            "By appointment, on invitation. Private presentation only.",
        },
        {
          eyebrow: "Commitment",
          body:
            "Regular edition, limited production on demand, followed by the maison.",
        },
      ],
    },

    collection: {
      eyebrow: "The collection",
      title: "Five pieces, one language.",
      intro:
        "Five models, five presences, five ways of inscribing movement into space. Maison Dodji does not juxtapose objects; the maison constructs a language. Each piece carries its own rhythm, silhouette and intensity.",
      asideLine1: "Collection prices.",
      asideLine2: "Private presentation by appointment.",
      renderingNote:
        "The visuals presented were intentionally developed as projection renderings, designed to express the customizable nature of each piece.",
      pieceLabel: "Piece N°",
      pieceCount: (n) => `${n} watch${n > 1 ? "es" : ""}`,
      cta: "Discover",
    },

    product: {
      back: "← Collection",
      pieceLabel: "Piece N°",
      specsTitle: "Specifications",
      specOrdinal: (i) => `0${i + 1}`,
      editionLabel: "Available by appointment",
      madeToOrder: "Each piece is made to order.",
      pricingFootnote:
        "*The indicated price represents the model's starting point. Finishes, colour combinations and personalization may affect the final price.",
      personalizationEyebrow: "Personalization",
      personalizationTitle: "Personalization",
      personalizationBody:
        "Colours, finishes and certain details may be personalized within the model's architecture and Maison Dodji's technical constraints. Specific requests are studied on a case-by-case basis.",
      personalizationCanChooseTitle: "You may choose",
      personalizationCanChoose: [
        "Colours",
        "Colour combinations",
        "Finishes",
      ],
      personalizationValidationTitle: "Subject to Maison Dodji validation",
      personalizationValidation: [
        "Special materials",
        "Graphics & inscriptions",
        "Particular constraints",
      ],
      personalizationArchitectureTitle:
        "The architecture remains unchanged",
      personalizationArchitecture: [
        "Proportions",
        "Structure",
        "Horological configuration",
      ],
      personalizationCta: "Consider a personalization → Private Studies",
      ctaContact: "Request this piece",
      ctaCollection: "View the collection",
      otherEyebrow: "Other pieces",
      otherTitle: "Recognize the piece that feels like you.",
      linkAll: "The full collection",
    },

    maison: {
      eyebrow: "The maison",
      title: "A Parisian maison devoted to singular furniture.",
      intro1:
        "Maison Dodji was born in Paris from a simple intuition: a private interior deserves pieces that install a presence. Singular furniture, drawn as forms — able to welcome a ritual, a material, a horological movement.",
      intro2:
        "At the intersection of design, furniture and horology, the maison conceives singular pieces — meant to inhabit space rather than to disappear within it. Each model is a form to recognize, a presence to welcome, a rhythm to settle in.",
      introAside:
        "To choose a Maison Dodji piece is not to own one more object. It is to recognize a form, to accept a rhythm, to make room for a presence.",
      gestureEyebrow: "The gesture",
      gestureTitle: "To inhabit the object, rather than to own it.",
      gestureBody:
        "Each piece is conceived as a form to inhabit: a volume that structures a place, a material in dialogue with light, an inner movement that becomes presence.",
      pillarsEyebrow: "Three axes",
      pillarsTitle: "Three axes structure the maison.",
      pillars: [
        {
          eyebrow: "Design",
          title: "Form and silhouette",
          body:
            "Pieces drawn for immediate reading, for the rightness of volume, for the precision of detail.",
        },
        {
          eyebrow: "Furniture",
          title: "Presence and place",
          body:
            "A singular piece of furniture that does not simply accompany&nbsp;: it structures, it inhabits, it settles in.",
        },
        {
          eyebrow: "Horology",
          title: "Movement and time",
          body:
            "Swiss mechanical modules in service of the mechanical watch, within a setting conceived to last.",
        },
      ],
      transmissionEyebrow: "Transmission",
      transmissionTitle:
        "An object conceived to live, to endure, to be passed on.",
      transmissionBody:
        "Each Maison Dodji piece is made to cross time — not as a product that wears out, but as a piece of furniture that settles in, that accompanies, that is passed on. The automatic movement finds its place; the maison finds its own.",
      transmissionCta: "View the collection",
      signatureAlt: "Lumumba — Maison Dodji piece",
    },

    univers: {
      eyebrow: "Universe",
      title: "One maison, one language, one world.",
      doctrine:
        "Maison Dodji conceives pieces of useful furniture in which the mechanical watch becomes presence, ritual and interior object.",
      intro:
        "The Maison Dodji universe is read in matter, in silhouette, in the way each piece takes its place within a space. The maison addresses those who compose their interior with exigence, and who wish to welcome horological movement within a piece of furniture conceived as a presence.",
      introAsideL1: "Furniture as frame.",
      introAsideL2: "Movement as rhythm.",
      introAsideL3: "Place as resonance.",
      markersEyebrow: "Coordinates",
      markersTitle: "Where the maison takes its place.",
      markers: [
        {
          num: "01",
          title: "Design",
          body:
            "A search for the right silhouette — sculptural without demonstration, present without saturation.",
        },
        {
          num: "02",
          title: "Furniture",
          body:
            "An object conceived as furniture&nbsp;: base, presence, duration. A piece of place, chosen for what it installs.",
        },
        {
          num: "03",
          title: "Horology",
          body:
            "Mechanical movement at the heart of the object&nbsp;: Swiss mechanical modules, automatic watches, continuous movement.",
        },
        {
          num: "04",
          title: "Interiors",
          body:
            "Quiet offices, composed salons, private interiors, places where each object is chosen with the exigence of a work.",
        },
      ],
      collabEyebrow: "Collaborations",
      collabTitle: "Presentations, places, dialogues.",
      collabBody1:
        "Maison Dodji welcomes a limited number of collaborations with interior architects, decorators, private places, exceptional residential projects, artists and galleries selected as spaces of presentation. Certain horological actors may also be associated when the dialogue respects the spirit of the maison. Each dialogue is built over time, around the piece — not around the transaction.",
      collabBody2:
        "Private presentations take place in dedicated venues or directly within clients' interiors. The maison travels when the piece justifies it.",
      collabSpacesEyebrow: "Spaces of collaboration",
      collabSpacesTitle: "Private staging.",
      collabSpacesBody:
        "Maison Dodji opens a space of collaboration with interior architects, decorators, galleries and private venues, in order to stage its pieces within denser, more inhabited environments — where the furniture enters into dialogue with materials, works, light and use.",
      stagingNoteEyebrow: "A light to come",
      stagingNote:
        "The current visuals present the pieces in an intentionally neutral light. Future collaborations will allow them to be set within denser interiors, expressing their relation to materials, works, furniture and places.",
      privateStudyEyebrow: "Private studies",
      privateStudyBody:
        "Certain studies open in private, following a conversation with the Maison. They are not visible on the site.",
      commissionsSignal:
        "Some Private Studies give rise to singular commissions conceived for a specific person, use and place. When a creation may be made public, Maison Dodji Paris progressively preserves its record as a Private Commission.",

      // ── Architecture of Mutation ─────────────────────────────────────
      mutationEyebrow: "Architecture of Mutation",
      mutationTitle:
        "Beyond the Private Study, certain visions call for an Architecture of Mutation.",
      mutationBody:
        "Certain intentions cannot be reached through colour, material or finish alone. Maison Dodji then intervenes on form itself. Starting from the functional core of an existing piece, an Architecture of Mutation is developed: new proportions, volumes, reliefs, junctions and envelope are conceived to bring forth a presence that did not exist before.",
      mutationCredo: [
        "The core remains.",
        "The envelope evolves.",
        "The presence mutates.",
      ],
      mutationPassageEyebrow: "The passage",
      mutationPassageTitle: "From interpretation to transformation.",
      mutationPassageBody:
        "The Architecture of Mutation is neither a personalisation option nor a new collection. It intervenes when a Private Study reaches a level of intention that requires going beyond the original architecture of the piece. The Maison Dodji form then becomes the starting point of a new construction.",
      mutationSequence: [
        {
          num: "01",
          title: "The vision",
          body: "Define what the piece is to become.",
        },
        {
          num: "02",
          title: "The core",
          body: "Preserve the Maison Dodji functional architecture.",
        },
        {
          num: "03",
          title: "The Architecture of Mutation",
          body: "Design the new volumes, junctions and proportions.",
        },
        {
          num: "04",
          title: "The envelope",
          body: "Give material form to this new architecture.",
        },
        {
          num: "05",
          title: "The mutation",
          body: "Bring the final piece into presence.",
        },
      ],
      mutationClosing:
        "A Private Study interprets a piece. An Architecture of Mutation may bring forth a new expression of it.",
      locations: [
        ["By appointment", "Private presentations"],
        ["On location", "By project"],
        ["By invitation", "Regular edition"],
      ],
      imageAlts: {
        sankara: "Sankara — horizontal presence",
        rosa: "Rosa — circular piece",
        quincy: "Quincy — density of the quartet",
      },
    },

    contact: {
      eyebrow: "Private Presentation",
      byAppointment: "By appointment",
      title: "Write to the Maison",
      intro:
        "For any private presentation, acquisition or collaboration enquiry, please write to the Maison via the form below or directly at:",
      asideL1: "A relationship,",
      asideL2: "not a transaction.",

      processEyebrow: "The process",
      steps: [
        {
          num: "01",
          title: "Initial conversation",
          body:
            "We respond within 48 hours. A first exchange allows us to understand your space, your intention, and the piece you have in mind.",
        },
        {
          num: "02",
          title: "Private presentation",
          body:
            "By appointment, in dedicated venues or directly within your interior when the project calls for it. The piece is presented in the context that will receive it.",
        },
        {
          num: "03",
          title: "Continued care",
          body:
            "Each piece is conceived, delivered and accompanied over time. The maison remains your sole interlocutor.",
        },
      ],

      asideEyebrow: "Contact",
      writeEyebrow: "Write to the maison",
      privateOrderEyebrow: "Private order",
      privateOrderPdfLabel: "Download the private order document",
      privateOrderPdfHint: "PDF · FR / EN · 2026 Cycle",

      form: {
        nom: "Name",
        nomPlaceholder: "Your name",
        email: "Email",
        emailPlaceholder: "you@address.com",
        ville: "City / Country",
        villePlaceholder: "Paris, France",
        objet: "Subject of inquiry",
        piece: "Piece of interest",
        pieceNoPreference: "— No preference",
        message: "Message",
        messagePlaceholder:
          "A few words on your project, your space, your intention…",
        note: "We will be in touch shortly.",
        submit: "Send the inquiry",
        submitting: "Sending…",
      },
      objects: [
        "Acquisition",
        "Personalized piece",
        "Private Study",
        "Collaboration",
        "Other",
      ],

      successEyebrow: "Message received",
      successTitle: "Thank you. The Maison will respond shortly.",
      successBody:
        "Your request has been received. Maison Dodji will get back to you as soon as possible to continue the conversation.",

      toastSuccess: "Your inquiry has been received by the maison.",
      toastErrorRequired: "Please complete the essential fields.",
      toastErrorGeneric: "Something went wrong. Please try again.",

      closingEyebrow: "Maison Dodji",
      closingTitle: "Singular furniture. Horological presence.",
      closingBody:
        "By appointment. The maison travels when the piece justifies it.",
    },
    space: {
      metaTitle: "Chapter — Maison Dodji",
      metaDescription: "Study chapter. Private space of Maison Dodji.",
      returnMaison: "Return to the Maison",
      sealedBanner: "Sealed chapter",
      sealedOn: "Sealed on",
      studyOpened: "Study opened on",
      chapters: {
        salutation: "Salutation",
        compris: "What the Maison has understood",
        references: "References and intentions",
        questions: "Questions to the Maison",
        rythme: "Rhythm",
        correspondance: "Correspondence",
        proposition: "Proposal",
        closing: "The chapter has closed.",
        welcome: "Welcome to your Maison Dodji Project Space",
        projectOwner: "The Project Owner",
        residence: "The Residence",
        architecture: "Architecture",
        watchCollection: "Watch Collection",
        existingSafe: "Existing Safe",
        futureCollection: "Future Collection",
        lifestyle: "Lifestyle",
        designReferences: "Design References",
        architect: "The Architect",
        interiorDesigner: "The Interior Designer",
        photos: "Photographs of the Place",
        measurements: "Measurements and Dimensions",
        technicalConstraints: "Technical Constraints",
        maisonObservations: "Observations from Maison Dodji",
        maisonQuestions: "Questions from the Maison",
        projectConclusion: "Project Conclusion",
      },
      fields: {
        salutation: "Salutation", body: "Introduction",
        fullName: "Name", city: "City", contact: "Contact",
        background: "Background",
        address: "Address", style: "Style", era: "Era",
        rooms: "Principal rooms", atmosphere: "Atmosphere",
        volumes: "Volumes", materials: "Materials", light: "Light",
        ambiance: "Ambiance",
        summary: "Summary", brand: "Brand", reference: "Reference",
        notes: "Notes", intent: "Intent", horizon: "Horizon",
        hasSafe: "Existing safe", model: "Model",
        capacity: "Capacity", location: "Location",
        limitations: "Limitations",
        rhythm: "Rhythm of life", travel: "Travel", interests: "Interests",
        imageUrl: "Image (URL)", caption: "Caption",
        name: "Name", agency: "Studio",
        label: "Area", dimensions: "Dimensions",
        access: "Access", electrical: "Electrical",
        humidity: "Humidity", security: "Security", other: "Other",
        prompt: "Question", nextSteps: "Next steps",
        observations: "Observations from the Maison",
        yes: "Yes", no: "No", na: "Not applicable",
        addItem: "Add",
      },
      brief: {
        docTitle: "Architectural Brief — Maison Dodji",
        eyebrow: "Maison Dodji — Architectural Brief",
        confidential: "Confidential document — Prepared by Maison Dodji",
        projectNumber: "Project number",
        client: "Client",
        title: "Project title",
        date: "Opening date",
        piece: "Piece",
        pieceNumber: "Piece number",
        summary: "Overview",
        chapters: "Completed chapters",
        openBrief: "Open the Architectural Brief",
        print: "Print",
      },
      colophon: {
        title: "Colophon",
        project: "Project",
        piece: "Piece",
        opened: "Study opened",
        sealed: "Chapter sealed",
        pieceNumber: "Piece number",
        signature: "Signature",
      },
      fixtureNotice:
        "Private technical fixture — not an authentic Maison Dodji project.",
      notFoundTitle: "This chapter does not exist.",
      notFoundBody:
        "Maison Dodji chapters are not public. They are opened for one person, by the Maison, after conversation.",
    },
    commission: {
      metaTitle: "Maison Dodji Paris — Private Commission",
      openingHouse: "Maison Dodji Paris",
      openingSeries: "Private Commission 01",
      returnMaison: "Return to the Maison",
      oneOfOne: "One of one",
      statusEyebrow: "Project status",
      chapterEyebrow: "Chapter",
      chaptersHeading: "Project chapters",
      finalHeading: "Final presentation",
      pieceLabel: "Piece",
      pieceNumberLabel: "Reference",
      closingSignatureLine1: "Maison Dodji Paris",
      closingSignatureLine2: "Singular furniture. Discreet authority.",
      closingSignatureLine3: "Time, in its place.",
      awaitingTitle: "This page is not yet open to the public.",
      awaitingBody:
        "Maison Dodji preserves the record of this creation. Its publication has not yet been authorised.",
    },
  },
};

export const getCopyright = (lang) => {
  const tpl = translations[lang].footer.copyright;
  return tpl.replace("{year}", String(new Date().getFullYear()));
};
